/**
 * auditLog.ts
 * Schreibt Änderungsprotokolle in die audit_log Tabelle.
 * Wird von DataContext bei jeder Mutation aufgerufen.
 */
import { supabase } from './supabase';

export type AuditAction = 'create' | 'update' | 'delete';
export type AuditEntityType = 'order' | 'customer' | 'offer' | 'invoice' | 'machine' | 'vehicle' | 'inventory' | 'employee';

export interface AuditEntry {
  id: string;
  tenant_id: string;
  user_id: string | null;
  user_name: string | null;
  entity_type: AuditEntityType;
  entity_id: string;
  action: AuditAction;
  changes: Record<string, { old?: any; new?: any }> | null;
  created_at: string;
}

let _tenantId: string | null = null;
let _userId: string | null = null;
let _userName: string | null = null;

/** Muss einmal beim App-Start aufgerufen werden (vom DataContext) */
export function initAuditContext(tenantId: string, userId: string | null, userName: string | null) {
  _tenantId = tenantId;
  _userId = userId;
  _userName = userName;
}

/**
 * Schreibt einen Audit-Log-Eintrag.
 * Bei 'update' werden nur die geänderten Felder protokolliert.
 */
export async function logAudit(
  entityType: AuditEntityType,
  entityId: string,
  action: AuditAction,
  changes?: Record<string, { old?: any; new?: any }> | null
): Promise<void> {
  if (!_tenantId) {
    console.warn('Audit: kein Tenant-Kontext gesetzt – Log übersprungen');
    return;
  }

  try {
    await supabase.from('audit_log').insert({
      tenant_id: _tenantId,
      user_id: _userId,
      user_name: _userName,
      entity_type: entityType,
      entity_id: entityId,
      action,
      changes: changes || null
    });
  } catch (err) {
    // Audit-Fehler dürfen die App nicht blockieren
    console.error('Audit-Log Fehler:', err);
  }
}

/**
 * Berechnet die Diff zwischen zwei Objekten.
 * Gibt nur geänderte Felder zurück.
 */
export function diffObjects(
  oldObj: Record<string, any>,
  newObj: Record<string, any>,
  ignoreFields: string[] = ['changeHistory', 'lockedAt', 'lockedBy', 'offlineVersion']
): Record<string, { old: any; new: any }> | null {
  const changes: Record<string, { old: any; new: any }> = {};

  for (const key of Object.keys(newObj)) {
    if (ignoreFields.includes(key)) continue;
    const oldVal = oldObj[key];
    const newVal = newObj[key];

    // Deep-compare für Arrays und Objekte
    const oldStr = JSON.stringify(oldVal);
    const newStr = JSON.stringify(newVal);

    if (oldStr !== newStr) {
      changes[key] = { old: oldVal, new: newVal };
    }
  }

  return Object.keys(changes).length > 0 ? changes : null;
}
